<template>
  <div
    class="product-card bg-white transition-all duration-300 rounded-xl border border-gray-100 h-full flex flex-col shadow-sm hover:shadow-md">
    <NuxtLink :to="productUrl" class="block flex-1">
      <!-- Resim Alanı -->
      <div class="relative aspect-[4/5] overflow-hidden bg-gray-50 rounded-t-xl">
        <NuxtImg :src="product.cover_image?.path
          ? 'cl/' + product.cover_image.path
          : '/img-placeholder.jpg'
          " :alt="product.name" format="webp" width="360" height="480" quality="80" loading="lazy" fit="inside"
          background="white" placeholder="/placeholder-card.webp" />

        <!-- Aksiyon Butonları -->
        <UButton
          class="absolute right-2 top-2 w-8 h-8 bg-white/90 text-slate-500  backdrop-blur-sm shadow-sm rounded-full flex items-center justify-center hover:bg-red-500 hover:text-white transition-all duration-200"
          :class="product.is_favorite ? 'text-red-500' : 'text-slate-500'"
          :icon="product.is_favorite ? 'i-heroicons-heart-solid' : 'i-heroicons-heart'"
          @click.prevent="$emit('toggle-favorite')" />


        <!-- İndirim Etiketi -->
        <div v-if="discountRate"
          class="absolute top-2 left-2 bg-orange-500 text-white text-xs font-medium px-2 py-1 rounded">
          %{{ discountRate }}
        </div>
      </div>

      <!-- İçerik Alanı -->
      <div class="p-3 flex flex-col">
        <!-- Marka -->
        <div class="text-xs font-medium text-orange-600 mb-1 line-clamp-1">
          {{ product.brand?.name }}
        </div>

        <!-- Ürün Adı -->
        <h3 class="font-medium text-gray-700 text-sm line-clamp-2 mb-2">
          {{ product.name }}
        </h3>

        <!-- Renk Seçenekleri Bilgisi -->
        <div v-if="hasMultipleColors" class="mt-auto">
          <div class="inline-flex items-center gap-1.5">
            <div class="w-4 h-4 rounded-full relative overflow-hidden">
              <div class="absolute inset-0 bg-gradient-to-r from-orange-500 to-orange-600"></div>
            </div>
            <span class="text-xs text-gray-500">{{ product.colors?.length }} renk</span>
          </div>
        </div>

        <!-- Yıldız Puanı -->
        <!-- <div class="flex items-center gap-1 mb-2">
          <div class="flex gap-px">
            <UIcon
              v-for="i in 5"
              :key="i"
              name="i-heroicons-star-solid"
              class="w-4 h-4 text-amber-400"
            />
          </div>
          <span class="text-xs text-gray-400">(97)</span>
        </div> -->
      </div>
    </NuxtLink>

    <!-- Fiyat ve Sepet Alanı -->
    <div class="p-3 border-t border-gray-100">
      <div class="flex items-center justify-between">
        <div>
          <div v-if="product.best_offer" class="flex items-center gap-1.5">
            <p class="text-sm sm:text-base font-semibold text-gray-900">
              {{ formatPrice(product.best_offer.sale_price) }}
            </p>

            <p v-if="hasDiscount" class="text-xs text-gray-400 line-through">
              {{ formatPrice(product.best_offer.price) }}
            </p>
          </div>
          <p v-else class="text-gray-500 italic text-xs">Fiyat Sorunuz</p>
        </div>

        <!-- Sepete Ekle Butonu -->
        <button
          class="w-7 h-7 sm:w-8 sm:h-8 bg-orange-500 text-white rounded-full flex items-center justify-center transform-gpu"
          @click.prevent="$emit('add-to-cart')">
          <UIcon name="i-heroicons-shopping-cart" class="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  product: {
    type: Object,
    required: true
  }
})


const route = useRoute()

// Birden fazla renk var mı kontrolü
const hasMultipleColors = computed(() => {
  return props.product.has_color && props.product.colors?.length > 1
})

// İndirim var mı kontrolü
const hasDiscount = computed(() => {
  return (
    props.product.sale_price && props.product.sale_price < props.product.price
  )
})

// İndirim yüzdesi hesaplama
const discountRate = computed(() => {
  if (!hasDiscount.value) return null
  const discount =
    ((props.product.price - props.product.sale_price) / props.product.price) *
    100
  return Math.round(discount)
})

// Ürün URL'si için computed property
const productUrl = computed(() => {
  const url = props.product?.canonical_url || ''
  if (!url) return url

  // Mağaza sayfasında olup olmadığımızı kontrol et
  const isVendorPage = route.path.includes('/magaza/')
  if (!isVendorPage) return url

  // URL pattern: /magaza/[slug]-m-[id]
  const match = route.path.match(/\/magaza\/.*-m-(\d+)$/)
  if (match && match[1]) {
    const vendorId = match[1]
    // Eğer URL'de mid parametresi yoksa ekle
    if (!url.includes('mid=')) {
      const separator = url.includes('?') ? '&' : '?'
      return `${url}${separator}mid=${vendorId}`
    }
  }

  return url
})

defineEmits(['add-to-cart', 'toggle-favorite'])
</script>

<style scoped>
.product-card {
  backface-visibility: hidden;
  transform: translate3d(0, 0, 0);
  -webkit-font-smoothing: subpixel-antialiased;
  contain: content;
  content-visibility: auto;
}

@media (prefers-reduced-motion: reduce) {
  .product-card * {
    animation: none !important;
    transition: none !important;
  }
}
</style>
